import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IPracticeMigration,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';
import {
  IStaffer,
  ISterilisationCycle,
  ISterilisationMachine,
  SterilisationStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  getError,
  snapshotCombineLatest,
  toNamedDocument,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { resolveMappedItem } from '../../../base-custom-mapping-handler';
import {
  BaseSterilisationCycleDestinationEntity,
  ISterilisationCycleJobData,
  ISterilistionCycleRecordMigrationData,
} from '../../../destination/entities/sterilisation-cycle';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import { STERILISATION_MACHINE_RESOURCE_TYPE } from '../../../mappings/sterilisation-machine';
import { TranslationMapHandler } from '../../../translation-map';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import {
  CorePracticeSterilisationCycle,
  SterilisationCycleSourceEntity,
} from '../../source/entities/sterilisation-cycles';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { CorePracticeSterilisationMachineMappingHandler } from '../mappings/sterilisation-machine';
import { StafferDestinationEntity } from './staff';

type CorePracticeSterilisationCycleJobData =
  ISterilisationCycleJobData<CorePracticeSterilisationCycle>;

export class CorePracticeSterilisationCycleDestinationEntity extends BaseSterilisationCycleDestinationEntity<CorePracticeSterilisationCycle> {
  sterilisationCycleSourceEntity = new SterilisationCycleSourceEntity();
  sterilisationMachineMappings =
    new CorePracticeSterilisationMachineMappingHandler();

  override sourceEntities = {
    sterilisationCycle: this.sterilisationCycleSourceEntity,
  };

  customMappings = {
    sterilisationMachineMapping: this.sterilisationMachineMappings,
    staff: new CorePracticeStafferMappingHandler(),
  };

  override destinationEntities = {
    staff: new StafferDestinationEntity(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<CorePracticeSterilisationCycleJobData[]> {
    const sterilisationMachineMappings$ =
      this.sterilisationMachineMappings.getRecords(translationMap);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([sterilisationMachineMappings$, staff$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sterilisationCycleSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([sourceSterilisationCycles, [sterilisationMachineMappings, staff]]) =>
          sourceSterilisationCycles.map((sourceSterilisationCycle) => ({
            sourceSterilisationCycle,
            sterilisationMachineMappings,
            staff,
          }))
      )
    );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: CorePracticeSterilisationCycleJobData
  ): Promise<
    | ISterilistionCycleRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourceCycle = data.sourceSterilisationCycle;

    try {
      const machine = await resolveMappedItem<ISterilisationMachine>(
        sourceCycle.data.steriliser,
        data.sterilisationMachineMappings,
        translationMap,
        STERILISATION_MACHINE_RESOURCE_TYPE
      );

      const stafferRef = getPractitionerOrDefaultMapping(
        sourceCycle.data.providerId,
        data.staff
      )?.destinationIdentifier;
      if (!stafferRef) {
        throw new Error(
          `Can't resolve staffer for cycle ${sourceCycle.data.cycleNumber}`
        );
      }
      const staffer = await Firestore.getDoc(stafferRef);

      const cycleRecord: ISterilisationCycle = {
        id: sourceCycle.data.cycleNumber,
        machine: toNamedDocument(machine),
        staffer: stafferToNamedDoc(staffer),
        name: sourceCycle.data.cycleNumber,
        runDate: sourceCycle.record.translations.runDate,
        status: SterilisationStatus.Passed,
        deleted: false,
      };

      return {
        sterilisationCycleRecord: {
          ...cycleRecord,
          sourceIdentifier:
            this.sterilisationCycleSourceEntity.getSourceRecordId(
              sourceCycle.data
            ),
          createdAt: sourceCycle.record.translations.runDate,
        },
        practiceRef: machine.practice.ref,
      };
    } catch (error) {
      return this._buildErrorResponse(sourceCycle, getError(error));
    }
  }

  buildSkippedResponse(
    data: CorePracticeSterilisationCycleJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceSterilisationCycle.record.uid,
      label: data.sourceSterilisationCycle.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourceSterilisationCycle.record.id,
    };
  }
}
